import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell } from '@/components/AppShell';
import { PageHeader } from '@/components/visual/PagePrimitives';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Plus, Pencil, Trash2, Star, Users, Inbox, ExternalLink, BarChart3, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import logger from '@/lib/logger';

const API = '/api';
const AX = { withCredentials: true };
const EMPTY = {
  name: '', title: '', company: '', bio: '', email: '', website: '', linkedin: '',
  rate_range: '', years_experience: 0, completed_projects: 0,
  specialties: '', regions: '', languages: '', certifications: '',
  is_featured: false, is_approved: true,
};

const toArr = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);
const fromArr = (a) => (a || []).join(', ');

export default function AdminConsultantsPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { localePath } = useLocale();
  usePageTitle('Admin · Consultants | ISMS Calculator');

  const [tab, setTab] = useState('consultants');
  const [consultants, setConsultants] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // consultant_id or null (new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const isAdmin = user?.is_admin;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, a] = await Promise.all([
        axios.get(`${API}/admin/consultants`, AX),
        axios.get(`${API}/admin/marketplace/requests`, AX),
        axios.get(`${API}/admin/marketplace/analytics`, AX),
      ]);
      setConsultants(c.data.consultants);
      setRequests(r.data.requests);
      setAnalytics(a.data);
    } catch (err) {
      logger.warn('[Admin] load failed:', err?.message || err);
      toast.error(t('visual.admin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin, loadData]);

  if (!authLoading && (!user || !user.is_admin)) {
    return <Navigate to={localePath('/')} replace />;
  }

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c.consultant_id);
    setForm({
      name: c.name || '', title: c.title || '', company: c.company || '', bio: c.bio || '',
      email: c.email || '', website: c.website || '', linkedin: c.linkedin || '',
      rate_range: c.rate_range || '', years_experience: c.years_experience || 0, completed_projects: c.completed_projects || 0,
      specialties: fromArr(c.specialties), regions: fromArr(c.regions), languages: fromArr(c.languages), certifications: fromArr(c.certifications),
      is_featured: !!c.is_featured, is_approved: c.is_approved !== false,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(), title: form.title.trim(), company: form.company.trim(), bio: form.bio.trim(),
      email: form.email.trim(), website: form.website.trim() || null, linkedin: form.linkedin.trim() || null,
      rate_range: form.rate_range.trim(),
      years_experience: parseInt(form.years_experience, 10) || 0,
      completed_projects: parseInt(form.completed_projects, 10) || 0,
      specialties: toArr(form.specialties), regions: toArr(form.regions),
      languages: toArr(form.languages), certifications: toArr(form.certifications),
      is_featured: form.is_featured, is_approved: form.is_approved,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/consultants/${editing}`, payload, AX);
        toast.success(t('visual.admin.updated'));
      } else {
        await axios.post(`${API}/admin/consultants`, payload, AX);
        toast.success(t('visual.admin.created'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('visual.admin.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const syncArticles = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/blog/backfill`, {}, AX);
      toast.success(t('visual.admin.syncSuccess', { ingested: data.ingested, fetched: data.fetched }));
    } catch (err) {
      logger.warn('[Admin] article sync failed:', err?.message || err);
      toast.error(err.response?.data?.detail || t('visual.admin.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('visual.admin.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/admin/consultants/${id}`, AX);
      toast.success(t('visual.admin.deleted'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('visual.admin.deleteError'));
    }
  };

  const approve = async (c) => {
    try {
      await axios.put(`${API}/admin/consultants/${c.consultant_id}`, {
        name: c.name || '', title: c.title || '', company: c.company || '', bio: c.bio || '',
        email: c.email || '', website: c.website || null, linkedin: c.linkedin || null,
        rate_range: c.rate_range || '',
        years_experience: c.years_experience || 0, completed_projects: c.completed_projects || 0,
        specialties: c.specialties || [], regions: c.regions || [], languages: c.languages || [], certifications: c.certifications || [],
        is_featured: !!c.is_featured, is_approved: true,
      }, AX);
      toast.success(t('visual.admin.published'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('visual.admin.publishError'));
    }
  };

  const tabBtn = (key, label, Icon, count) => (
    <button
      onClick={() => setTab(key)}
      data-testid={`admin-tab-${key}`}
      aria-pressed={tab === key}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === key ? 'bg-[#527F19] text-white shadow-card' : 'text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7]'}`}
    >
      <Icon className="w-4 h-4" /> {label} <span className="text-xs opacity-70">({count})</span>
    </button>
  );

  const renderBody = () => {
    if (loading) {
      return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#527F19]" /></div>;
    }
    if (tab === 'consultants') {
      return (
        <div className="space-y-3" data-testid="admin-consultants-list">
          {consultants.map((c) => (
            <div key={c.consultant_id} className="bg-white border border-[#DDE1E5] rounded-lg p-4 flex items-center gap-4 transition-colors hover:border-[#527F19]/35" data-testid={`admin-consultant-${c.consultant_id}`}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[#F5F6F7] text-[#171A1D] font-semibold text-sm shrink-0">
                {c.avatar_initials || c.name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-semibold text-[#171A1D] truncate">{c.name}</span>
                  {c.is_featured && <span className="inline-flex items-center gap-1 text-xs text-[#527F19]"><Star className="w-3 h-3 fill-current" />{t('visual.admin.featured')}</span>}
                  {!c.is_approved && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">{t('visual.admin.unpublished')}</span>}
                  {c.source === 'application' && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{t('visual.admin.applied')}</span>}
                  {c.is_sample && <span className="text-xs px-1.5 py-0.5 rounded bg-[#B7791F]/[0.12] text-[#8A5A0B]">{t('visual.admin.sample')}</span>}
                </div>
                <p className="text-xs text-[#8A929B] truncate">{c.title} · {c.company} · {(c.languages || []).join(', ')}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!c.is_approved && (
                  <Button variant="ghost" size="sm" onClick={() => approve(c)} className="text-[#527F19]" data-testid={`admin-approve-${c.consultant_id}`} title={t('visual.admin.publish')} aria-label={t('visual.admin.publish')}><CheckCircle2 className="w-4 h-4" /></Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`admin-edit-${c.consultant_id}`} title={t('visual.edit')} aria-label={t('visual.edit')}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c.consultant_id)} className="text-[#B42318]" data-testid={`admin-delete-${c.consultant_id}`} title={t('visual.delete')} aria-label={t('visual.delete')}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (tab === 'requests') {
      return (
        <div className="space-y-3" data-testid="admin-requests-list">
          {requests.length === 0 ? (
            <p className="text-sm text-[#66707A] py-10">{t('visual.admin.noRequests')}</p>
          ) : requests.map((r) => (
            <div key={r.request_id} className="bg-white border border-[#DDE1E5] rounded-lg p-4 transition-colors hover:border-[#527F19]/35" data-testid={`admin-request-${r.request_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-display font-semibold text-[#171A1D]">{r.name} <span className="text-[#8A929B] font-normal">· {r.company || '—'}</span></span>
                <span className="text-xs text-[#8A929B]">{(r.created_at || '').slice(0, 10)}</span>
              </div>
              <p className="text-sm text-[#527F19] mt-0.5">
                <a href={`mailto:${r.email}`} className="hover:underline inline-flex items-center gap-1">{r.email} <ExternalLink className="w-3 h-3" /></a>
                <span className="text-[#66707A]"> → {r.consultant_name}</span>
              </p>
              {r.message && <p className="text-sm text-[#3A4249] mt-2 bg-[#F5F6F7] rounded-lg p-3">{r.message}</p>}
            </div>
          ))}
        </div>
      );
    }
    const a = analytics || { total_requests: 0, last_30_days: 0, last_7_days: 0, active_consultants: 0, top_consultants: [], timeseries: [] };
    const maxTop = Math.max(1, ...a.top_consultants.map((x) => x.count));
    const maxDay = Math.max(1, ...a.timeseries.map((d) => d.count));
    const stats = [
      ['totalLeads', a.total_requests],
      ['last30', a.last_30_days],
      ['last7', a.last_7_days],
      ['activeConsultants', a.active_consultants],
    ];
    return (
      <div className="space-y-6" data-testid="admin-analytics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map(([label, val]) => (
            <div key={label} className="bg-white border border-[#DDE1E5] rounded-lg p-4 transition-colors hover:border-[#527F19]/35">
              <div className="font-data text-3xl font-bold text-[#171A1D]" data-testid={`stat-${label}`}>{val}</div>
              <div className="text-xs text-[#66707A] mt-1.5">{t(`visual.admin.${label}`)}</div>
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#DDE1E5] rounded-lg p-5">
          <h2 className="mb-4">{t('visual.admin.topLeads')}</h2>
          {a.top_consultants.length === 0 ? (
            <p className="text-sm text-[#66707A]">{t('visual.admin.noLeads')}</p>
          ) : a.top_consultants.map((x) => (
            <div key={x.consultant_id} className="flex items-center gap-3 mb-2">
              <span className="admin-chart-label">{x.name}</span>
              <div className="flex-1 bg-[#F5F6F7] rounded h-5 overflow-hidden">
                <div className="h-full rounded bg-[#171A1D]" style={{ width: `${(x.count / maxTop) * 100}%` }} />
              </div>
              <span className="text-xs font-semibold text-[#171A1D] w-8 text-right">{x.count}</span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#DDE1E5] rounded-lg p-5">
          <h2 className="mb-4">{t('visual.admin.leadsOverTime')}</h2>
          <div className="flex items-end gap-1 h-28">
            {a.timeseries.map((d) => (
              <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 bg-[#527F19]/80 hover:bg-[#527F19] rounded-t transition-colors" style={{ height: `${Math.max(4, (d.count / maxDay) * 100)}%` }} />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-[#8A929B] mt-2">
            <span>{a.timeseries[0]?.date}</span><span>{a.timeseries[a.timeseries.length - 1]?.date}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppShell>
      <div className="admin-workspace" data-testid="admin-consultants-page">
      <div className="workspace-main-width">
        <PageHeader compact title={t('visual.admin.title')} description={t('visual.admin.description')} eyebrow={t('visual.admin.eyebrow')} testId="admin-page-title" actions={<>
            <Button
              onClick={syncArticles}
              disabled={syncing}
              variant="outline"
              className="border-[#DDE1E5] text-[#3A4249] hover:text-[#171A1D] font-semibold"
              data-testid="admin-sync-articles-btn"
              title={t('visual.admin.syncHint')}
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {syncing ? t('visual.admin.syncing') : t('visual.admin.sync')}
            </Button>
            {tab === 'consultants' && (
              <Button onClick={openNew} className="bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold" data-testid="admin-add-consultant-btn">
                <Plus className="w-4 h-4 mr-1" /> {t('visual.admin.add')}
              </Button>
            )}
          </>} />

        <div className="admin-tabs">
          {tabBtn('consultants', t('visual.admin.consultants'), Users, consultants.length)}
          {tabBtn('requests', t('visual.admin.requests'), Inbox, requests.length)}
          {tabBtn('analytics', t('visual.admin.analytics'), BarChart3, analytics?.total_requests ?? 0)}
        </div>

        {renderBody()}
      </div>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-consultant-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editing ? t('visual.admin.edit') : t('visual.admin.add')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            {[
              ['name', 'name', 'text', true], ['title', 'jobTitle', 'text', false], ['company', 'company', 'text', false],
              ['email', 'email', 'email', true], ['website', 'website', 'text', false], ['linkedin', 'linkedin', 'text', false],
              ['rate_range', 'rate', 'text', false],
            ].map(([key, label, type, req]) => (
              <div key={key}>
                <Label htmlFor={`admin-${key}`} className="text-sm">{t(`visual.admin.${label}`)}{req ? ' *' : ''}</Label>
                <Input id={`admin-${key}`} type={type} required={req} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1" data-testid={`admin-field-${key}`} />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="admin-years">{t('visual.admin.years')}</Label><Input id="admin-years" data-testid="admin-field-years" type="number" min="0" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className="mt-1" /></div>
              <div><Label htmlFor="admin-projects">{t('visual.admin.projects')}</Label><Input id="admin-projects" data-testid="admin-field-projects" type="number" min="0" value={form.completed_projects} onChange={(e) => setForm({ ...form, completed_projects: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label htmlFor="admin-bio">{t('visual.admin.bio')}</Label><Textarea id="admin-bio" data-testid="admin-field-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 min-h-[70px]" /></div>
            {['specialties', 'regions', 'languages', 'certifications'].map(key => (
              <div key={key}>
                <Label htmlFor={`admin-${key}`} className="text-sm">{t(`visual.admin.${key}`)}</Label>
                <Input id={`admin-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1" data-testid={`admin-field-${key}`} />
              </div>
            ))}
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v === true })} data-testid="admin-field-featured" /> {t('visual.admin.featured')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.is_approved} onCheckedChange={(v) => setForm({ ...form, is_approved: v === true })} data-testid="admin-field-approved" /> {t('visual.admin.visible')}
              </label>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold" data-testid="admin-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? t('visual.admin.save') : t('visual.admin.create')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </AppShell>
  );
}
